import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { FloppyDisk, X } from "@phosphor-icons/react";

export const fillTemplate = (text, client = {}) => (text || "")
  .replaceAll("{name}", client.name || "")
  .replaceAll("{first_name}", (client.name || "").split(" ")[0])
  .replaceAll("{email}", client.email || "");

let cache = null;
const listeners = new Set();
export async function loadMessageTemplates(force = false) {
  if (!cache || force) {
    const { data } = await api.get("/admin/message-templates");
    cache = data;
    listeners.forEach(fn => fn(cache));
  }
  return cache;
}

export default function MessageTemplatePicker({ client, current, onApply, bodyOnly = false, testid = "msg-tpl" }) {
  const [templates, setTemplates] = useState(cache || []);
  const [naming, setNaming] = useState(false);
  const [name, setName] = useState("");
  useEffect(() => {
    listeners.add(setTemplates);
    loadMessageTemplates().then(setTemplates).catch(() => {});
    return () => listeners.delete(setTemplates);
  }, []);

  const apply = (id) => {
    const t = templates.find(x => x.template_id === id);
    if (!t) return;
    onApply({ subject: fillTemplate(t.subject, client), body: fillTemplate(t.body, client) });
    toast.success(`Applied "${t.name}"`);
  };
  const save = async () => {
    if (!name.trim()) return;
    try {
      await api.post("/admin/message-templates", {
        name: name.trim(), subject: bodyOnly ? "" : (current.subject || ""), body: current.body || "",
      });
      await loadMessageTemplates(true);
      toast.success("Template saved");
      setNaming(false); setName("");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed"); }
  };

  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid={testid}>
      <select value="" onChange={(e) => apply(e.target.value)} data-testid={`${testid}-select`}
        className="px-3 py-2 text-xs flex-1 min-w-[160px]">
        <option value="">{templates.length ? "Apply a template…" : "No templates yet"}</option>
        {templates.map(t => <option key={t.template_id} value={t.template_id}>{t.name}</option>)}
      </select>
      {naming ? (
        <div className="flex items-center gap-1">
          <input autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="Template name"
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); save(); } if (e.key === "Escape") setNaming(false); }}
            data-testid={`${testid}-name`} className="px-3 py-2 text-xs w-40" />
          <button type="button" onClick={save} data-testid={`${testid}-save-confirm`} className="btn-primary text-xs h-8">Save</button>
          <button type="button" onClick={() => setNaming(false)} aria-label="Cancel" className="btn-ghost text-xs h-8"><X size={11} weight="bold" /></button>
        </div>
      ) : (
        <button type="button" disabled={!current.body?.trim()} onClick={() => setNaming(true)}
          data-testid={`${testid}-save`} className="btn-ghost text-xs h-8" title="Save current text as a template">
          <FloppyDisk size={11} weight="bold" /> Save as template
        </button>
      )}
    </div>
  );
}
